import { BreadCrumbs, Chip, Container, Tabs } from 'components'
import { CARRIER_DETAILS_VIEW, carrierDetailsNBTabs, carrierStatus } from 'constants/status'
import { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { Typography, Box, Paper, ToggleButton, ToggleButtonGroup, IconButton, Tooltip } from '@mui/material'
import { InfoOutlined, Language } from '@mui/icons-material'
import colors from 'theme/colors'
import { formatDate, isShortcodeExcludedForMileageLocationReporting } from 'tools/helper'
import strings from 'strings'
import { getActiveUnitsWithCameras } from 'api/carriers'
import LoadingDots from 'components/loadingDots'
import { reportException } from 'tools/logs'
import { useLocation } from 'react-router'
import { useCarrierCodeBootstrap } from 'hooks/useCarrierCodeBootstrap'
import NBITelematics from '../../nbitelematics'
import NbDetails from '../../nbDetails'
import NbMilesByZip from '../../nbMilesByZip'
import NbRadiusOfOperation from '../../nbRadiusOfOperation'
import NbSafetyEvents from '../../nbSafetyEvents'
import DriversDashboard from '../../drivers'
import MileageReportingDashboard from 'pages/reporting/partial/mileageReporting'
import LocationReportingDashboard from 'pages/reporting/partial/locationReporting'
import UbiSummaryDashboard from '../../ubiSummary'
import { isCarrierLogin } from 'tools/auth'
import LanguageSwitcher from 'components/languageSwitcher'
import { REPORT_KEYS, getReportFlagKey } from 'constants/reporting'
import { useFeatureFlag } from 'contexts/ReportingContext'

const t = strings.NB_TABLE_DEFAULTS
const nbDetails = strings.NB_CARRIER_DETAILS
const NBTABS = strings.CARRIERS.NBTABS
const UBI_SUMMARY_LABEL = strings.CARRIERS.TABS.UBI_SUMMARY


interface ICarrierDetailNBProps {
    changeCarrierDetailsView?: () => void
}

const CarrierDetailsNB: React.FC<ICarrierDetailNBProps> = ({ changeCarrierDetailsView }) => {
    const location = useLocation()
    const path = location.pathname
    const carrierData = useCarrierCodeBootstrap()
    const isCarrierView = isCarrierLogin()
    const viewTestId = isCarrierView ? 'carrier-details-nb-view-carrier' : 'carrier-details-nb-view-insurance'
    const [activeUnitsWithCamerasLoading, setActiveUnitsWithCamerasLoading] =
        useState<boolean>(true)
    const [activeUnitsWithCameras, setActiveUnitsWithCameras] = useState<number>()
    const [carrierDetailsTab, setCarrierDetailsTab] = useState(0)
    const [locale, setLocale] = useState<'en' | 'fr'>('en')
    const carrierInfoRef = useRef<HTMLDivElement>(null)

    // UBI Summary is offered as the first tab when its feature flag is on (insurer-level, resolved
    // by carrier-api against the portal company). Applies to this NB view, which is what a carrier
    // with the NB shortcode sees at /fleet-reporting (and the insurer "Fleet view").
    const ubiEnabled = useFeatureFlag(getReportFlagKey(REPORT_KEYS.UBI_SUMMARY))

    // NorthBridge subdomains (managed via REACT_APP_EXCLUDED_SHORTCODES_FOR_MILEAGE_LOCATION_REPORTING)
    // do not show Mileage / Location Reporting — applies to both the NorthBridge carrier view and
    // the insurer "Fleet view" (both render this component).
    const visibleNBTabs = useMemo(() => {
        const base = !isShortcodeExcludedForMileageLocationReporting()
            ? carrierDetailsNBTabs
            : carrierDetailsNBTabs.filter(
                (t) => t.label !== NBTABS.NB_MILEAGE_REPORTING && t.label !== NBTABS.NB_LOCATION_REPORTING
            )
        return ubiEnabled ? [{ label: UBI_SUMMARY_LABEL }, ...base] : base
    }, [ubiEnabled])

    const enhancedCarrierDetailsNBTabs = useMemo(() => {
        const tabTestIds: Record<string, string> = {
            [NBTABS.NBI_TELEMATICS_SCORE]: 'carrier-details-nb-tab-telematics',
            [NBTABS.NB_DETAILS]: 'carrier-details-nb-tab-details',
            [NBTABS.NB_MILES_BY_ZIP]: 'carrier-details-nb-tab-miles-by-zip',
            [NBTABS.NB_RADIUS_OF_OPERATION]: 'carrier-details-nb-tab-radius-of-operation',
            [NBTABS.NB_SAFETY_EVENTS]: 'carrier-details-nb-tab-safety-events',
            [NBTABS.NB_MILEAGE_REPORTING]: 'carrier-details-nb-tab-mileage-reporting',
            [NBTABS.NB_LOCATION_REPORTING]: 'carrier-details-nb-tab-location-reporting',
            [UBI_SUMMARY_LABEL]: 'carrier-details-nb-tab-ubi-summary',
        }
        return visibleNBTabs.map(tab => {
            const base = {
                ...tab,
                dataTestId: tabTestIds[tab.label],
            }
            if (tab.label === NBTABS.NB_MILES_BY_ZIP || tab.label === NBTABS.NB_RADIUS_OF_OPERATION) {
                return {
                    ...base,
                    icon: <Language fontSize="small" sx={{ ml: 0.5 }} />
                }
            }
            return base
        })
    }, [visibleNBTabs])
    useEffect(() => {
      const carrierId =
        carrierData?.carrierId || localStorage.getItem('companyId')
      if (!carrierId) {
        setActiveUnitsWithCamerasLoading(false)
        return
      }
      setActiveUnitsWithCamerasLoading(true)
      getActiveUnitsWithCameras(carrierId)
        .then((data: any) => {
          setActiveUnitsWithCameras(data?.totalWithCameras || 0)
        })
        .catch(reportException)
        .finally(() => setActiveUnitsWithCamerasLoading(false))
    }, [carrierData?.carrierId])

    const today = new Date().toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
    })

    const NbTabPanel = useCallback(() => {
        const currentTabLabel = visibleNBTabs[carrierDetailsTab]?.label
        switch (currentTabLabel) {
            case UBI_SUMMARY_LABEL:
                return <UbiSummaryDashboard />
            case NBTABS.NBI_TELEMATICS_SCORE:
                return <NBITelematics />
            case NBTABS.NB_DETAILS:
                return <NbDetails />
            case NBTABS.NB_MILES_BY_ZIP:
                return <NbMilesByZip locale={locale} />
            case NBTABS.NB_RADIUS_OF_OPERATION:
                return <NbRadiusOfOperation locale={locale} />
            case NBTABS.NB_SAFETY_EVENTS:
                return <NbSafetyEvents />
            case NBTABS.NB_DRIVERS:
                return <DriversDashboard useKilometers={true} />
            case NBTABS.NB_MILEAGE_REPORTING:
                return <MileageReportingDashboard useCarrierFilters={true} />
            case NBTABS.NB_LOCATION_REPORTING:
                return <LocationReportingDashboard useCarrierFilters={true} />
            default:
                return null
        }
    }, [carrierDetailsTab, locale, visibleNBTabs])

    const getPageMargins = () => {
        return `@page { margin: 30px!important; }`
    }

    return (
        <Container sx={{ px: 1 }} data-testid="carrier-details-nb-page">
            <span data-testid={viewTestId} aria-hidden="true" style={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }} />
            <Container>
                <Box sx={{ mt: 2 }} data-testid="carrier-details-nb-header-section">
                    <Paper
                        elevation={0}
                        sx={{
                            p: 3,
                            mb: 3,
                            border: '1px solid #E0E0E0',
                            borderRadius: '8px',
                        }}
                        data-testid="carrier-details-nb-info-paper"
                    >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }} data-testid="carrier-details-nb-header-row">
                            <Box data-testid="carrier-details-nb-breadcrumbs">
                                <BreadCrumbs path={path} />
                            </Box>
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                <Tooltip 
                                    title={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            This will only change the languages for the tabs with the language icon
                                            <Language fontSize="small" />
                                        </Box>
                                    } 
                                    arrow
                                >
                                    <IconButton size="small" sx={{ color: colors.grey }}>
                                        <InfoOutlined fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <LanguageSwitcher locale={locale} onLocaleChange={setLocale} />
                            {changeCarrierDetailsView && (
                                <ToggleButtonGroup
                                    value={CARRIER_DETAILS_VIEW.FLEET}
                                    exclusive
                                    onChange={(_, newView) => newView === CARRIER_DETAILS_VIEW.INSURANCE && changeCarrierDetailsView()}
                                    sx={{
                                        flexShrink: 0,
                                        backgroundColor: colors.platinum,
                                        borderRadius: '6px',
                                        padding: '1px',
                                        border: `1px solid ${colors.silver}`,
                                        '& .MuiToggleButtonGroup-grouped': {
                                            border: 0,
                                            borderRadius: '4px',
                                            px: 1.5,
                                            py: 0.5,
                                            fontSize: '0.8125rem',
                                            fontWeight: 600,
                                            textTransform: 'none',
                                            '&.Mui-selected': {
                                                backgroundColor: colors.white,
                                                color: colors.primary,
                                                boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                                                '&:hover': {
                                                    backgroundColor: colors.white,
                                                    color: colors.primary,
                                                },
                                            },
                                            '&:not(.Mui-selected)': {
                                                color: colors.grey,
                                                backgroundColor: 'transparent',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                                    color: colors.primary,
                                                },
                                            },
                                        },
                                    }}
                                >

                                    <ToggleButton
                                        value={CARRIER_DETAILS_VIEW.FLEET}
                                        data-testid="carrier-details-nb-toggle"
                                        aria-label={strings.CARRIERS.FLEET_VIEW}
                                    >
                                        {strings.CARRIERS.FLEET_VIEW}
                                    </ToggleButton>
                                    <ToggleButton value={CARRIER_DETAILS_VIEW.INSURANCE} data-testid="carrier-details-nb-toggle-fleet" aria-label={strings.CARRIERS.CHANGE_TO_INSURANCE_VIEW}>
                                        {strings.CARRIERS.CHANGE_TO_INSURANCE_VIEW}
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            )}
                            </Box>
                        </Box>
                        <Box ref={carrierInfoRef} sx={{ mt: 3, mb: 2, ml: 0 }} data-testid="carrier-details-nb-info-block">
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                                <Typography variant="h4" sx={{ fontWeight: 'bold' }} data-testid="carrier-details-nb-value-carrier-name">
                                    {localStorage.getItem('carrierName')}
                                </Typography>
                                {!isCarrierView && (
                                    <Chip
                                        data-testid="carrier-details-nb-value-carrier-status"
                                        label={nbDetails.CARRIER_STATUS_LABEL + (carrierStatus[localStorage.getItem('carrierStatus') as keyof typeof carrierStatus]?.label ?? t.DASH_VALUE)}
                                        variant="filled"
                                        sx={{
                                            backgroundColor: carrierStatus[localStorage.getItem('carrierStatus') as keyof typeof carrierStatus]?.background ?? '#E3E6E8',
                                            color: carrierStatus[localStorage.getItem('carrierStatus') as keyof typeof carrierStatus]?.color ?? '#707A8B',
                                            fontSize: '1rem',
                                            padding: '8px 16px',
                                            height: 'auto',
                                            '& .MuiChip-label': {
                                                padding: '4px 8px',
                                            }
                                        }}
                                    />
                                )}
                            </Box>
                            <Box sx={{ display: 'flex', gap: 4 }} data-testid="carrier-details-nb-info-fields">
                                {!isCarrierView && (
                                    <Typography variant="body1" color="text.secondary">
                                        {nbDetails.TRUCKERCLOUD_ONBOARDING_DATE} <Typography component="span" color="text.secondary" variant="body1" data-testid="carrier-details-nb-value-onboarding-date">{localStorage.getItem('onboardingDate') ? formatDate(localStorage.getItem('onboardingDate') || '', false) : nbDetails.EMPTY_VALUE}</Typography>
                                    </Typography>
                                )}
                                <Typography variant="body1" color="text.secondary">
                                    {nbDetails.EFFECTIVE_DATE} <Typography component="span" color="text.secondary" variant="body1" data-testid="carrier-details-nb-value-effective-date">{today}</Typography>
                                </Typography>
                                {!isCarrierView && (
                                    <Typography variant="body1" color="text.secondary">
                                        {nbDetails.TSP} <Typography component="span" color="text.secondary" variant="body1" data-testid="carrier-details-nb-value-tsp">{localStorage.getItem('eldProviderRisk')}</Typography>
                                    </Typography>
                                )}
                                <Typography variant="body1" color="text.secondary">
                                    {nbDetails.DOT}
                                    <Typography component="span" color="text.secondary" variant="body1" data-testid="carrier-details-nb-value-dot">{carrierData?.carrierCodeType === 'DOT' && carrierData?.carrierCodeValue
                                        ? nbDetails.US_DOT_PREFIX + carrierData?.carrierCodeValue
                                        : ' ' + nbDetails.EMPTY_VALUE}</Typography>
                                </Typography>
                                <Typography variant="body1" color="text.secondary">
                                    {nbDetails.POLICY_ID}
                                    <Typography component="span" color="text.secondary" variant="body1" data-testid="carrier-details-nb-value-policy-id">{carrierData?.carrierPolicyId && carrierData?.carrierPolicyId !== '-'
                                        ? ' ' + carrierData?.carrierPolicyId
                                        : ' ' + nbDetails.EMPTY_VALUE}</Typography>
                                </Typography>
                                <Typography variant="body1" color="text.secondary">
                                    {nbDetails.UNITS_WITH_CAMERAS}{' '}
                                    <Typography component="span" color="text.secondary" variant="body1" data-testid="carrier-details-nb-value-units-with-cameras">{activeUnitsWithCamerasLoading ? (
                                        <LoadingDots color="#666666" data-testid="carrier-details-nb-loading-units-with-cameras" />
                                    ) : (
                                        activeUnitsWithCameras ?? nbDetails.EMPTY_VALUE
                                    )}</Typography>
                                </Typography>
                            </Box>
                        </Box>
                    </Paper>
                </Box>
            </Container >
            <Container data-testid="carrier-details-nb-tabs-container">
                <Tabs
                    tabData={enhancedCarrierDetailsNBTabs}
                    value={carrierDetailsTab}
                    id={enhancedCarrierDetailsNBTabs[carrierDetailsTab].label}
                    handleChange={(_e, val) => setCarrierDetailsTab(val)}
                    sxContainer={{
                        width: '100%',
                        borderBottom: '1px solid #e0e0e0',
                    }}
                    sxTabs={{
                        '& .MuiTabs-indicator': {
                            backgroundColor: '#0072BC',
                            height: '3px',
                        },
                    }}
                />
                <Container data-testid="carrier-details-nb-tab-panel">
                    <NbTabPanel />
                </Container>
                <style>{getPageMargins()}</style>
            </Container>
        </Container>
    )
}

export default CarrierDetailsNB